import { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';

import PageHeader from '../../components/PageHeader';
import {
  useGetClassificationPatternsQuery,
  useGetPatternEffectivenessQuery,
  useGetPendingPatternMatchesQuery,
  useGetRecentValidatedKeywordsQuery,
  useCreateClassificationPatternMutation,
  useUpdateClassificationPatternMutation,
  useDeleteClassificationPatternMutation,
  useRecordPatternMatchFeedbackMutation,
  useGeneratePatternsFromKeywordsMutation,
  useActivateProbationaryPatternMutation,
  useDeleteProbationaryPatternMutation,
  GetClassificationPatternsDocument,
  GetPatternEffectivenessDocument,
  GetPendingPatternMatchesDocument,
  GetRecentValidatedKeywordsDocument,
} from '../../gql/generated/graphql';

import PatternMetricsCards from './components/PatternMetricsCards';
import PatternsDataGrid from './components/PatternsDataGrid';
import PatternFormDialog from './components/PatternFormDialog';
import PendingMatchesDataGrid from './components/PendingMatchesDataGrid';
import ValidatedKeywordsDataGrid from './components/ValidatedKeywordsDataGrid';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const SignalTrackingPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [targetFilter, setTargetFilter] = useState<string>('all');
  const [showTestPatternsOnly, setShowTestPatternsOnly] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPattern, setEditingPattern] = useState<any>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  // Queries
  const {
    data: patternsData,
    loading: patternsLoading,
    error: patternsError,
    refetch: refetchPatterns,
  } = useGetClassificationPatternsQuery({
    pollInterval: 60000,
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: effectivenessData,
    loading: effectivenessLoading,
    refetch: refetchEffectiveness,
  } = useGetPatternEffectivenessQuery({
    variables: { minMatches: 1 },
    pollInterval: 60000,
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: pendingMatchesData,
    loading: pendingMatchesLoading,
    refetch: refetchPendingMatches,
  } = useGetPendingPatternMatchesQuery({
    variables: { limit: 100 },
    pollInterval: 60000,
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: validatedKeywordsData,
    loading: validatedKeywordsLoading,
    refetch: refetchValidatedKeywords,
  } = useGetRecentValidatedKeywordsQuery({
    variables: { limit: 500, days: 90 },
    pollInterval: 60000,
    fetchPolicy: 'cache-and-network',
  });

  // Mutations
  const [createPattern, { loading: createLoading }] = useCreateClassificationPatternMutation({
    refetchQueries: [
      { query: GetClassificationPatternsDocument },
      { query: GetPatternEffectivenessDocument, variables: { minMatches: 1 } },
    ],
    awaitRefetchQueries: true,
  });

  const [updatePattern, { loading: updateLoading }] = useUpdateClassificationPatternMutation({
    refetchQueries: [
      { query: GetClassificationPatternsDocument },
      { query: GetPatternEffectivenessDocument, variables: { minMatches: 1 } },
    ],
    awaitRefetchQueries: true,
  });

  const [deletePattern] = useDeleteClassificationPatternMutation({
    refetchQueries: [
      { query: GetClassificationPatternsDocument },
      { query: GetPatternEffectivenessDocument, variables: { minMatches: 1 } },
    ],
    awaitRefetchQueries: true,
  });

  const [recordFeedback] = useRecordPatternMatchFeedbackMutation({
    refetchQueries: [
      { query: GetPendingPatternMatchesDocument, variables: { limit: 100 } },
      { query: GetPatternEffectivenessDocument, variables: { minMatches: 1 } },
    ],
    awaitRefetchQueries: true,
  });

  const [generatePatternsFromKeywords, { loading: generatePatternLoading }] = useGeneratePatternsFromKeywordsMutation({
    refetchQueries: [
      { query: GetClassificationPatternsDocument },
      { query: GetPatternEffectivenessDocument, variables: { minMatches: 1 } },
      { query: GetRecentValidatedKeywordsDocument, variables: { limit: 500, days: 90 } },
    ],
    awaitRefetchQueries: true,
  });

  const [activateProbationaryPattern] = useActivateProbationaryPatternMutation({
    refetchQueries: [
      { query: GetClassificationPatternsDocument },
      { query: GetPatternEffectivenessDocument, variables: { minMatches: 1 } },
    ],
    awaitRefetchQueries: true,
  });

  const [deleteProbationaryPattern] = useDeleteProbationaryPatternMutation({
    refetchQueries: [
      { query: GetClassificationPatternsDocument },
      { query: GetPatternEffectivenessDocument, variables: { minMatches: 1 } },
    ],
    awaitRefetchQueries: true,
  });

  // Computed values
  const patterns = useMemo(() => {
    let filtered = patternsData?.classificationPatterns || [];

    // Apply target filter
    if (targetFilter !== 'all') {
      filtered = filtered.filter(p => p?.classificationTarget === targetFilter);
    }

    // Apply test pattern filter
    if (showTestPatternsOnly) {
      filtered = filtered.filter(p => p?.isProbationary === true);
    }

    return filtered;
  }, [patternsData, targetFilter, showTestPatternsOnly]);

  const metrics = useMemo(() => {
    const allPatterns = patternsData?.classificationPatterns || [];
    const effectiveness = effectivenessData?.patternEffectiveness || [];

    const totalPatterns = allPatterns.length;
    const activePatterns = allPatterns.filter(p => p?.isActive).length;
    const totalMatches = effectiveness.reduce((sum, e) => sum + (e?.totalMatches || 0), 0);

    const patternsWithPrecision = effectiveness.filter(e => e?.precisionPct !== null && e?.precisionPct !== undefined);
    const avgPrecision =
      patternsWithPrecision.length > 0
        ? patternsWithPrecision.reduce((sum, e) => sum + (e?.precisionPct || 0), 0) / patternsWithPrecision.length
        : null;

    return { totalPatterns, activePatterns, totalMatches, avgPrecision };
  }, [patternsData, effectivenessData]);

  const pendingMatches = useMemo(() => {
    return (pendingMatchesData?.pendingPatternMatches || []).filter(m => m !== null) as any[];
  }, [pendingMatchesData]);

  const validatedKeywords = useMemo(() => {
    return (validatedKeywordsData?.recentValidatedKeywords || []).filter(k => k !== null) as any[];
  }, [validatedKeywordsData]);

  // Handlers
  const handleRefresh = () => {
    refetchPatterns();
    refetchEffectiveness();
    refetchPendingMatches();
    refetchValidatedKeywords();
  };

  const handleOpenCreateDialog = () => {
    setEditingPattern(null);
    setMutationError(null);
    setDialogOpen(true);
  };

  const handleEditPattern = (pattern: any) => {
    setEditingPattern(pattern);
    setMutationError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPattern(null);
    setMutationError(null);
  };

  const handleSavePattern = async (formData: any) => {
    try {
      setMutationError(null);

      if (editingPattern) {
        const result = await updatePattern({
          variables: {
            patternId: editingPattern.dbId,
            input: {
              weight: formData.weight,
              description: formData.description,
              isActive: editingPattern.isActive,
            },
          },
        });
        if (!result.data?.updateClassificationPattern?.success) {
          throw new Error(result.data?.updateClassificationPattern?.error || 'Update failed');
        }
      } else {
        const result = await createPattern({
          variables: {
            input: {
              patternText: formData.patternText,
              patternType: formData.patternType,
              classificationTarget: formData.classificationTarget,
              weight: formData.weight,
              description: formData.description,
            },
            createdBy: 'ui_user',
          },
        });
        if (!result.data?.createClassificationPattern?.success) {
          throw new Error(result.data?.createClassificationPattern?.error || 'Create failed');
        }
      }

      handleCloseDialog();
    } catch (error: any) {
      setMutationError(error.message);
    }
  };

  const handleDeletePattern = async (patternId: number) => {
    const result = await deletePattern({ variables: { patternId } });
    if (!result.data?.deleteClassificationPattern?.success) {
      throw new Error(result.data?.deleteClassificationPattern?.error || 'Delete failed');
    }
  };

  const handleToggleActive = async (patternId: number, isActive: boolean) => {
    await updatePattern({
      variables: {
        patternId,
        input: { isActive },
      },
    });
  };

  const handleMarkCorrect = async (matchId: number) => {
    await recordFeedback({
      variables: {
        patternMatchId: matchId,
        wasCorrect: true,
        userFeedback: 'helpful',
      },
    });
  };

  const handleMarkIncorrect = async (matchId: number) => {
    await recordFeedback({
      variables: {
        patternMatchId: matchId,
        wasCorrect: false,
        userFeedback: 'misleading',
      },
    });
  };

  const handleGeneratePattern = async (keyword: string, classificationType: 'software' | 'non_software') => {
    const result = await generatePatternsFromKeywords({
      variables: {
        keywords: [keyword],
        createdBy: 'ui_user',
        autoActivate: false,
        classificationTarget: classificationType === 'software' ? 'software' : 'services',
      },
    });

    if (!result.data?.generatePatternsFromKeywords?.result?.success) {
      throw new Error(result.data?.generatePatternsFromKeywords?.result?.message || 'Pattern generation failed');
    }
  };

  const handleActivateProbationary = async (patternId: number) => {
    const result = await activateProbationaryPattern({ variables: { patternId } });
    if (!result.data?.activateProbationaryPattern?.success) {
      throw new Error(result.data?.activateProbationaryPattern?.error || 'Activation failed');
    }
  };

  const handleDeleteProbationary = async (patternId: number) => {
    const result = await deleteProbationaryPattern({ variables: { patternId } });
    if (!result.data?.deleteProbationaryPattern?.success) {
      throw new Error(result.data?.deleteProbationaryPattern?.error || 'Delete failed');
    }
  };

  // Loading state
  const isLoading = patternsLoading && !patternsData;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Error state
  if (patternsError && !patternsData) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh">
        <Typography variant="h6" color="error">
          Error loading signal tracking data
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          {patternsError.message}
        </Typography>
        <Button variant="outlined" color="primary" onClick={handleRefresh} sx={{ mt: 2 }} startIcon={<RefreshIcon />}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Signal Tracking"
        subtitle="Manage classification patterns and view signal effectiveness metrics"
        variant="default"
        actions={[
          {
            label: 'Add Pattern',
            onClick: handleOpenCreateDialog,
            variant: 'contained',
            icon: <AddIcon />,
          },
        ]}
      />

      <Box sx={{ p: 3 }}>
        {patternsError && patternsData && (
          <Paper
            sx={{
              p: 2,
              mb: 2,
              bgcolor: alpha('#FF564F', 0.1),
              borderLeft: '4px solid #FF564F',
            }}
          >
            <Typography variant="body2" color="error">
              Unable to fetch latest data. Showing cached results.
            </Typography>
          </Paper>
        )}

        {/* Metrics Cards */}
        <PatternMetricsCards
          totalPatterns={metrics.totalPatterns}
          activePatterns={metrics.activePatterns}
          totalMatches={metrics.totalMatches}
          avgPrecision={metrics.avgPrecision}
        />

        {/* Tabs and Actions Row */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Box sx={{ flex: 1, minWidth: 0, mr: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-scrollButtons': {
                  '&.Mui-disabled': { opacity: 0.3 },
                },
              }}
            >
              <Tooltip
                title="All classification patterns: active patterns for company classification, and test patterns (marked TEST) requiring activation"
                placement="bottom"
              >
                <Tab label={`Patterns (${patterns.length})`} />
              </Tooltip>
              <Tooltip title="Pattern matches awaiting validation feedback" placement="bottom">
                <Tab label={`Pending Matches (${pendingMatches.length})`} />
              </Tooltip>
              <Tooltip
                title="Keywords from your validation work - generate new patterns from high-frequency keywords"
                placement="bottom"
              >
                <Tab label={`Validated Keywords (${validatedKeywords.length})`} />
              </Tooltip>
            </Tabs>
          </Box>

          <Box display="flex" gap={2} alignItems="center" sx={{ flexShrink: 0 }}>
            {activeTab === 0 && (
              <>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Filter by Target</InputLabel>
                  <Select value={targetFilter} label="Filter by Target" onChange={e => setTargetFilter(e.target.value)}>
                    <MenuItem value="all">All Targets</MenuItem>
                    <MenuItem value="software">Software</MenuItem>
                    <MenuItem value="services">Services</MenuItem>
                    <MenuItem value="reseller">Reseller</MenuItem>
                    <MenuItem value="agency">Agency</MenuItem>
                    <MenuItem value="non_software">Non-Software</MenuItem>
                  </Select>
                </FormControl>
                <Tooltip title="Show only test patterns requiring activation">
                  <Chip
                    label="TEST patterns only"
                    color={showTestPatternsOnly ? 'warning' : 'default'}
                    onClick={() => setShowTestPatternsOnly(!showTestPatternsOnly)}
                    variant={showTestPatternsOnly ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                  />
                </Tooltip>
              </>
            )}
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
              Refresh
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Tab Panels */}
        <TabPanel value={activeTab} index={0}>
          <Paper
            sx={{
              p: 2,
              mb: 2,
              bgcolor: '#F6ECE1',
              border: '1px solid #3F5D66',
            }}
          >
            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
              <strong>Classification Patterns:</strong> Active patterns automatically classify companies during
              crawling. Patterns marked with{' '}
              <Chip label="TEST" size="small" color="warning" sx={{ mx: 0.5, height: 18, fontSize: '0.65rem' }} /> are
              test patterns requiring your review. Toggle the switch to activate/deactivate regular patterns. For test
              patterns, use the circular check icon to activate or trash icon to delete.
            </Typography>
          </Paper>
          <Paper sx={{ height: 600 }}>
            <PatternsDataGrid
              patterns={patterns.filter((p): p is NonNullable<typeof p> => p !== null)}
              onEdit={handleEditPattern}
              onDelete={handleDeletePattern}
              onToggleActive={handleToggleActive}
              onActivateProbationary={handleActivateProbationary}
              onDeleteProbationary={handleDeleteProbationary}
              loading={patternsLoading}
            />
          </Paper>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Paper
            sx={{
              p: 2,
              mb: 2,
              bgcolor: '#F6ECE1',
              border: '1px solid #3F5D66',
            }}
          >
            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
              <strong>Pending Matches:</strong> Review pattern matches that need your validation feedback. Click the
              green check icon if the pattern correctly classified the company as non-software, or the red X icon if it
              was a false positive (company is actually software). Your feedback improves pattern accuracy.
            </Typography>
          </Paper>
          <Paper sx={{ height: 600 }}>
            <PendingMatchesDataGrid
              matches={pendingMatches}
              onMarkCorrect={handleMarkCorrect}
              onMarkIncorrect={handleMarkIncorrect}
              loading={pendingMatchesLoading}
            />
          </Paper>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Paper
            sx={{
              p: 2,
              mb: 2,
              bgcolor: '#F6ECE1',
              border: '1px solid #3F5D66',
            }}
          >
            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
              <strong>Validated Keywords:</strong> These keywords come from your company validation work. High-frequency
              keywords indicate strong classification signals. Click the sparkles icon to generate new test patterns
              from promising keywords. Generated patterns will appear in the Patterns tab marked with{' '}
              <Chip label="TEST" size="small" color="warning" sx={{ mx: 0.5, height: 18, fontSize: '0.65rem' }} /> and
              require activation.
            </Typography>
          </Paper>
          <Paper sx={{ height: 600 }}>
            <ValidatedKeywordsDataGrid
              keywords={validatedKeywords}
              onGeneratePattern={handleGeneratePattern}
              loading={validatedKeywordsLoading}
            />
          </Paper>
        </TabPanel>
      </Box>

      {/* Create/Edit Dialog */}
      <PatternFormDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSavePattern}
        editPattern={editingPattern}
        loading={createLoading || updateLoading}
        error={mutationError}
      />
    </Box>
  );
};

export default SignalTrackingPage;
